import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AgentNotesList from './AgentNotesList';
import { useAlert } from './AlertContext';

import { useAssistantNoteApi } from '../Services/AssistantNoteApiProvider';
import { AssistantNoteApi } from '../Services/AssistantNoteApiProviderTypes';
import useTablePagination from '../Hooks/useTablePagination';

function AgentNotesTab({ agentId, canEdit }: { agentId: string; canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const { showAlert } = useAlert();
  const { getAssistantNotes, exportAssistantNotes } = useAssistantNoteApi();

  const [notes, setNotes] = useState<AssistantNoteApi[]>([]);
  const [totalNotes, setTotalNotes] = useState<number>(0);
  const [notesLoading, setNotesLoading] = useState<boolean>(false);
  const [onUpdate, setOnUpdate] = useState<boolean>(false);

  const tableTitle = 'Agent Notes';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const updateTrigger = (): void => setOnUpdate((prev) => !prev);

  const fetchNotes = useCallback(
    async (skip: number, limit: number) => {
      setNotesLoading(true);
      try {
        const responseData = await getAssistantNotes(agentId, skip, limit);
        setNotes(responseData.items || []);
        setTotalNotes(responseData.totalCount || 0);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setNotes([]);
          setTotalNotes(0);
        }
      } finally {
        setNotesLoading(false);
      }
    },
    [agentId, getAssistantNotes],
  );

  const handleTableChange = (skip: number, limit: number): void => {
    fetchNotes(skip, limit);
  };

  const handleOpenRun = (note: AssistantNoteApi): void => {
    if (!note.run_key) return;
    navigate(
      `/organizations/${organizationId}/agents-hub/${agentId}/audit-operations/${note.run_key}`,
    );
  };

  const handleExport = async (noteIds: string[]): Promise<void> => {
    if (noteIds.length === 0) return;
    try {
      await exportAssistantNotes(agentId, noteIds);
    } catch (error) {
      showAlert('fail', 'Failed to export the notes.');
    }
  };

  useEffect(() => {
    fetchNotes(storedSkip(), storedLimit());
  }, [onUpdate, storedSkip, storedLimit]);

  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <div className="gap d-flex flex-row-to-col flex-column">
        <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
          <AgentNotesList
            canEdit={canEdit}
            notes={notes}
            onUpdateTrigger={updateTrigger}
            onOpenRun={handleOpenRun}
            onExport={handleExport}
            loading={notesLoading}
            totalNotes={totalNotes}
            persistedPageSize={initialPageSize}
            tableTitle={tableTitle}
            onChangePage={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
}

export default AgentNotesTab;
